import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FixedNav,
  Caption,
  Card,
  Button,
  Divider,
  StatStrip,
  AnimatedNumber,
  PulseDot,
  OnboardingModal,
  MODULE_SLIDES,
  ENDGAME_SOURCE_KIND_LABEL_FR,
} from '@/ui';
import { endgameApi, type EndgameListItem } from './api';

/**
 * Endgame Forge — liste des finales (port V2).
 *
 * Source proto : `docs/prototypes/ui-kit-v2/ui_kits/the_chess_coach/EndgameForge.jsx`
 * §EndgameList. Pattern visuel identique à Tactics Dojo (header PulseDot +
 * StatStrip + filter tabs + grid groupée par thème), accent amber au lieu
 * de cyan (DESIGN §2.3 — L'Architecte).
 *
 * Wiring : `useQuery(['endgames', 'list'])` (existant MVP1.2) avec filtre
 * outcome optionnel côté client (`win | draw | defend`).
 */

const THEME_META: Record<string, { label: string; desc: string }> = {
  pawn_endgame: {
    label: 'Finales de pions',
    desc: 'Opposition · règle du carré · triangulation · breakthrough',
  },
  rook_endgame: {
    label: 'Finales de tours',
    desc: 'Lucena · Philidor · Vančura · R+P vs R',
  },
  queen_endgame: {
    label: 'Finales de dames',
    desc: 'Q+P vs Q · perpétuel · conversion lente',
  },
  bishop_endgame: {
    label: 'Finales de fous',
    desc: 'Couleurs opposées · bon/mauvais fou',
  },
  knight_endgame: {
    label: 'Finales de cavaliers',
    desc: 'Sauts brisés · zones de contrôle',
  },
  minor_piece: {
    label: 'Pièces mineures',
    desc: 'Fou vs Cavalier · bon/mauvais fou',
  },
  conversion: {
    label: 'Technique de conversion',
    desc: "Transformer +1.5 d'eval en victoire sans rendre l'avantage",
  },
};

const OUTCOME_LABEL: Record<EndgameListItem['target_outcome'], string> = {
  win: 'Gagner',
  draw: 'Annuler',
  defend: 'Défendre',
};

const OUTCOME_COLOR: Record<EndgameListItem['target_outcome'], string> = {
  win: 'var(--amber-500)',
  draw: 'var(--ink-300)',
  defend: 'var(--paper-50)',
};

type OutcomeFilter = 'all' | EndgameListItem['target_outcome'];

export function EndgameListPage(): JSX.Element {
  const navigate = useNavigate();
  const listQ = useQuery({
    queryKey: ['endgames', 'list'],
    queryFn: () => endgameApi.list({ limit: 200 }),
  });

  const all = useMemo(() => listQ.data?.endgames ?? [], [listQ.data]);
  const total = all.length;
  const counts = useMemo(
    () => ({
      win: all.filter((e) => e.target_outcome === 'win').length,
      draw: all.filter((e) => e.target_outcome === 'draw').length,
      defend: all.filter((e) => e.target_outcome === 'defend').length,
    }),
    [all],
  );

  const filters: Array<{ id: OutcomeFilter; label: string; count: number }> = [
    { id: 'all', label: 'Tous', count: total },
    { id: 'win', label: 'Gagner', count: counts.win },
    { id: 'draw', label: 'Annuler', count: counts.draw },
  ];
  if (counts.defend > 0) {
    filters.push({ id: 'defend', label: 'Défendre', count: counts.defend });
  }

  const [filter, setFilter] = useFilter();

  const visible = filter === 'all' ? all : all.filter((e) => e.target_outcome === filter);

  const grouped = useMemo(() => {
    const acc: Record<string, EndgameListItem[]> = {};
    for (const e of visible) {
      (acc[e.theme] ??= []).push(e);
    }
    return Object.entries(acc);
  }, [visible]);

  const chips = [
    { label: 'Finales', value: String(total), color: 'var(--paper-50)' },
    { label: 'Gagner', value: String(counts.win), color: 'var(--amber-500)' },
  ];

  return (
    <>
      {/* T7 MVP2.5 — onboarding 1ère visite Endgame Forge (bug A8). */}
      <OnboardingModal
        moduleSlug="endgame-forge"
        moduleLabel="Endgame Forge"
        tone="amber"
        slides={MODULE_SLIDES['endgame-forge'] ?? []}
      />
      <FixedNav chips={chips} />
      <main className="mx-auto max-w-[1280px] px-6 py-8 flex flex-col gap-6">
        <header className="flex flex-col gap-2">
          <div className="flex items-center gap-2.5">
            <PulseDot color="var(--amber-500)" size={8} />
            <Caption color="amber">Endgame Forge</Caption>
          </div>
          <h1 className="font-display text-display-2 text-paper-50">Finales</h1>
          <p className="text-body-sm text-ink-300">
            {total} finales sourcées Syzygy / Müller-Lamprecht. Les coups attendus sont
            prouvés (tablebase ou Stockfish d≥30).
          </p>
        </header>

        <StatStrip className="flex-wrap items-stretch gap-x-8">
          <StatTile label="Finales" value={total} color="var(--paper-50)" />
          <Divider vertical />
          <div className="flex flex-col gap-1 min-w-[140px]">
            <Caption>Objectifs</Caption>
            <div className="font-mono text-base leading-none" style={{ fontWeight: 500 }}>
              <span className="text-amber-500">{counts.win}</span>
              <span className="text-ink-700 mx-1">·</span>
              <span className="text-ink-300">{counts.draw}</span>
              <span className="text-ink-700 mx-1">·</span>
              <span className="text-paper-50">{counts.defend}</span>
            </div>
            <span className="font-mono text-[10px] text-ink-300 uppercase tracking-wider">
              gagner · annuler · défendre
            </span>
          </div>
          <Divider vertical />
          <div className="flex flex-col gap-1 min-w-[110px]">
            <Caption>Cible MVP1</Caption>
            <span className="font-mono text-base text-amber-500 leading-none" style={{ fontWeight: 500 }}>
              {total} / 20
            </span>
          </div>
          <Divider vertical />
          <div className="flex flex-col gap-1 min-w-[220px]">
            <Caption>Sources officielles</Caption>
            <span className="font-mono text-[11px] leading-snug text-ink-300">
              <span className="text-paper-50">Syzygy</span> tablebase ·{' '}
              <span className="text-paper-50">Müller-Lamprecht</span>
              <br />
              <em>Fundamental Chess Endings</em> · validé Stockfish d≥30
            </span>
          </div>
          <div className="ml-auto self-center">
            <Button
              variant="primary"
              accent="amber"
              disabled={total === 0}
              onClick={() => {
                const pick = all[Math.floor(Math.random() * all.length)];
                if (pick) navigate(`/endgame-forge/${encodeURIComponent(pick.slug)}`);
              }}
            >
              🔨 Exercice aléatoire
            </Button>
          </div>
        </StatStrip>

        {/* Filter tabs */}
        <nav className="flex gap-2 flex-wrap">
          {filters.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-lg border font-display text-sm transition-all duration-150 ${
                  active
                    ? 'border-amber-500 bg-amber-500/10 text-paper-50'
                    : 'border-ink-700 bg-ink-800 text-ink-300 hover:border-ink-600 hover:text-paper-50'
                }`}
              >
                {f.label}
                <span className="ml-1.5 font-mono text-xs opacity-70">{f.count}</span>
              </button>
            );
          })}
        </nav>

        {listQ.isPending && (
          <Card>
            <p className="text-ink-300 italic">Chargement des finales…</p>
          </Card>
        )}
        {listQ.isError && (
          <Card>
            <p className="text-coral-500">
              Aucune finale chargée. Préviens Valentin.
            </p>
          </Card>
        )}
        {listQ.data && listQ.data.count === 0 && (
          <Card>
            <p className="text-ink-300 italic">
              Aucune finale active. La table sera peuplée au prochain deploy.
            </p>
          </Card>
        )}

        {grouped.length > 0 && (
          <div className="flex flex-col gap-7">
            {grouped.map(([themeId, items]) => {
              const meta = THEME_META[themeId];
              return (
                <section key={themeId}>
                  <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                    <h2 className="font-display text-heading-3 text-amber-500">
                      {meta?.label ?? themeId}
                    </h2>
                    {meta?.desc && (
                      <span className="text-body-sm text-ink-300 italic">{meta.desc}</span>
                    )}
                  </div>
                  <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 list-none p-0">
                    {items.map((e) => (
                      <li key={e.slug}>
                        <EndgameTile
                          endgame={e}
                          onClick={() => navigate(`/endgame-forge/${encodeURIComponent(e.slug)}`)}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        )}

        <footer className="pt-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-caption uppercase text-ink-300 hover:text-paper-50 transition-all duration-150"
          >
            ← Mission Control
          </button>
        </footer>
      </main>
    </>
  );
}

// ─── Sous-composants ──────────────────────────────────────────────────────

function StatTile({
  label,
  value,
  color,
  format,
}: {
  label: string;
  value: number;
  color: string;
  format?: (v: number) => string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1 min-w-[80px]">
      <Caption>{label}</Caption>
      <AnimatedNumber
        value={value}
        {...(format ? { format } : {})}
        className="font-mono"
        style={{ fontSize: 26, color, lineHeight: 1, fontWeight: 500 }}
      />
    </div>
  );
}

function EndgameTile({
  endgame,
  onClick,
}: {
  endgame: EndgameListItem;
  onClick: () => void;
}): JSX.Element {
  const outColor = OUTCOME_COLOR[endgame.target_outcome];
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left flex flex-col gap-2.5 p-5 bg-ink-800 border border-ink-700 rounded-xl transition-all duration-150 hover:border-amber-500/50 hover:shadow-card-hover animate-card-enter w-full"
    >
      <div className="flex justify-between items-center">
        <span
          className="text-caption uppercase"
          style={{ color: outColor, letterSpacing: '0.05em', fontWeight: 500 }}
        >
          {OUTCOME_LABEL[endgame.target_outcome]}
          {endgame.eval_at_start ? ` · ${endgame.eval_at_start}` : ''}
        </span>
        <span className="font-mono text-[11px] text-ink-300">
          {endgame.piece_count} pièces
        </span>
      </div>
      <h3 className="font-display text-heading-3 text-paper-50 leading-snug">
        {endgame.name}
      </h3>
      {endgame.sub_theme && (
        <p className="font-mono text-xs text-ink-300">{endgame.sub_theme}</p>
      )}
      <div className="mt-auto pt-1 flex justify-between items-center font-mono text-[11px] text-ink-300">
        <span>{ENDGAME_SOURCE_KIND_LABEL_FR[endgame.source_kind] ?? endgame.source_kind}</span>
        <span className="text-amber-500">Exercice →</span>
      </div>
    </button>
  );
}

function useFilter(): [OutcomeFilter, (f: OutcomeFilter) => void] {
  const [v, set] = useState<OutcomeFilter>('all');
  return [v, set];
}
